import { useEffect, useState } from 'react';
import env from '../../config/env';
import './AutocompleteMapInput.css';

function Icon({ name, className = '' }) {
  const paths = {
    'location-outline': (
      <>
        <path d="M12 21s7-6.2 7-12a7 7 0 1 0-14 0c0 5.8 7 12 7 12Z" />
        <circle cx="12" cy="9" r="2.5" />
      </>
    ),
    'navigate-outline': <path d="m12 21 4-9 5-5-9 4-4 9 4-4Z" />,
    'airplane-outline': (
      <path d="M10.8 2.8 14 2l1.1 7.2 5.1 2.3c.5.2.8.7.8 1.2v.8h-6.2l-.9 5.7-2.1 1-1.2-6.7H5.5l-2.8-1.5v-1.2l6-2.2L10.8 2.8Z" />
    ),
    'train-outline': (
      <>
        <rect x="5" y="3" width="14" height="15" rx="3" />
        <path d="M8 21h2l2-3 2 3h2M8 8h8M8 12h2m4 0h2" />
      </>
    ),
    'bus-outline': (
      <>
        <rect x="5" y="3" width="14" height="16" rx="3" />
        <path d="M5 10h14M8 16h.01M16 16h.01M8 21l2-2m6 2-2-2" />
      </>
    ),
    'business-outline': (
      <>
        <path d="M4 21V5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16M16 8h2a2 2 0 0 1 2 2v11M2 21h20" />
        <path d="M8 7h2m2 0h2M8 11h2m2 0h2M8 15h2m2 0h2" />
      </>
    ),
    'close-outline': (
      <>
        <path d="M6 6l12 12M18 6 6 18" />
      </>
    ),
    'alert-circle-outline': (
      <>
        <circle cx="12" cy="12" r="9" />
        <path d="M12 8v5M12 16h.01" />
      </>
    ),
    'search-outline': (
      <>
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m16 16 5 5" />
      </>
    ),
    'chevron-forward-outline': <path d="m9 6 6 6-6 6" />,
  };

  return (
    <svg
      className={`mc-icon ${className}`}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {paths[name] || paths['location-outline']}
    </svg>
  );
}

function AutocompleteMapInput({ handleSelectLocation, resetInput }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [show, setShow] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [sessionToken, setSessionToken] = useState(() => crypto.randomUUID());

  /* =========================================================
     ICON
     ========================================================= */

  const getIconName = (suggestion) => {
    const source = suggestion?.properties?.source;

    if (source === 'current_location') return 'navigate-outline';
    if (source === 'airport') return 'airplane-outline';
    if (source === 'train_station') return 'train-outline';
    if (source === 'bus_station') return 'bus-outline';
    if (source === 'transit_station') return 'train-outline';
    if (source === 'place') return 'business-outline';

    return 'location-outline';
  };

  /* =========================================================
     RESET
     ========================================================= */

  useEffect(() => {
    if (resetInput) {
      setSearchTerm('');
      setSearchResults([]);
      setShow(false);
    }
  }, [resetInput]);

  /* =========================================================
     PLACE DETAILS
     ========================================================= */

  const getPlaceDetails = async (address) => {
    try {
      const jsonData = address;

      return {
        name: jsonData.properties.full_address,
        address: jsonData.properties.full_address,
        location: [
          jsonData.properties.coordinates.longitude,
          jsonData.properties.coordinates.latitude,
        ],
      };
    } catch (e) {
      setError(e);
    }
  };

  /* =========================================================
     SELECT LOCATION
     ========================================================= */

  const handleSelectLocationInput = async (suggestion) => {
    setSearchResults([]);

    const placeDetails = await getPlaceDetails(suggestion);

    if (!placeDetails) return;

    setSearchTerm(placeDetails.name);
    handleSelectLocation(placeDetails);
    setShow(false);

    // Nouvelle session après sélection
    // setSessionToken(crypto.randomUUID());
  };

  /* =========================================================
     CLEAR
     ========================================================= */

  const handleClearInput = () => {
    setSearchTerm('');
    setShow(false);
    setSearchResults([]);
    setError(null);
  };

  /* =========================================================
     NORMALIZE SUGGESTION
     ========================================================= */

  const toSuggestionShape = (full_address, lng, lat, extra = {}) => ({
    properties: {
      full_address,
      coordinates: {
        longitude: lng,
        latitude: lat,
      },
      ...extra,
    },
  });

  /* =========================================================
     SEARCH
     ========================================================= */

  const handleInputChange = (event) => {
    const inputValue = event.target.value;

    setSearchTerm(inputValue);

    if (inputValue.length >= 2) {
      const fetchData = async () => {
        setLoading(true);
        setError(null);

        try {
          /* ---------------------------------------------------
             1. POI TRANSPORT
          --------------------------------------------------- */

          const poiSuggestUrl =
            `${env.baseUrlApi}/mapbox/suggest` +
            `?q=${encodeURIComponent(inputValue)}` +
            `&session_token=${sessionToken}` +
            `&limit=8&types=poi` +
            `&poi_category=${encodeURIComponent(
              'airport,train_station,transit_station,bus_station'
            )}`;

          /* ---------------------------------------------------
             2. VILLE FALLBACK
          --------------------------------------------------- */

          const placeUrl =
            `${env.baseUrlApi}/mapbox/geocoding` + `?q=${encodeURIComponent(inputValue)}&limit=3`;

          const [poiRes, placeRes] = await Promise.all([fetch(poiSuggestUrl), fetch(placeUrl)]);

          if (!poiRes.ok || !placeRes.ok) {
            throw new Error('Network response was not ok');
          }

          const poiData = await poiRes.json();
          const placeData = await placeRes.json();

          /* ---------------------------------------------------
             3. POI
          --------------------------------------------------- */

          const poiSuggestions = poiData?.suggestions ?? [];

          const poiRetrievePromises = poiSuggestions.map(async (suggestion) => {
            const retrieveUrl =
              `${env.baseUrlApi}/mapbox/retrieve/` +
              `${encodeURIComponent(suggestion.mapbox_id)}` +
              `?session_token=${sessionToken}`;

            const response = await fetch(retrieveUrl);

            if (!response.ok) return null;

            const data = await response.json();
            const feature = data?.features?.[0];
            const coords = feature?.geometry?.coordinates;

            const lng = coords?.[0];
            const lat = coords?.[1];

            if (typeof lng !== 'number' || typeof lat !== 'number') {
              return null;
            }

            const label =
              feature?.properties?.full_address ||
              `${feature?.properties?.name || suggestion.name}${
                suggestion.place_formatted ? `, ${suggestion.place_formatted}` : ''
              }`;

            const categories = feature?.properties?.poi_category || [];

            let source = 'poi';

            if (categories.includes('airport')) {
              source = 'airport';
            } else if (categories.includes('train_station')) {
              source = 'train_station';
            } else if (categories.includes('bus_station')) {
              source = 'bus_station';
            } else if (categories.includes('transit_station')) {
              source = 'transit_station';
            }

            return toSuggestionShape(label, lng, lat, {
              source,
              mapbox_id: suggestion.mapbox_id,
            });
          });

          const poiItemsRaw = await Promise.all(poiRetrievePromises);

          const poiItems = poiItemsRaw.filter(Boolean);

          /* ---------------------------------------------------
             4. VILLES
          --------------------------------------------------- */

          const placeItems = (placeData?.features ?? [])
            .filter((feature) => Array.isArray(feature?.center) && feature.center.length === 2)
            .map((feature) =>
              toSuggestionShape(feature.place_name, feature.center[0], feature.center[1], {
                source: 'place',
              })
            );

          /* ---------------------------------------------------
             5. POSITION ACTUELLE
          --------------------------------------------------- */

          const currentLocationItem = toSuggestionShape('Position actuelle', 0, 0, {
            source: 'current_location',
          });

          setSearchResults([currentLocationItem, ...poiItems, ...placeItems]);

          setShow(true);
        } catch (e) {
          setError(e);
          setSearchResults([]);
          setShow(true);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    } else {
      setSearchResults([]);
      setShow(false);
      setError(null);
    }
  };

  /* =========================================================
     CURRENT POSITION
     ========================================================= */

  const handleSelectCurrentPosition = () => {
    if (!navigator.geolocation) {
      setError(new Error('Geolocation not supported'));
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lng = position.coords.longitude;
        const lat = position.coords.latitude;

        const suggestion = {
          properties: {
            full_address: 'Position actuelle',
            coordinates: {
              longitude: lng,
              latitude: lat,
            },
            source: 'current_location',
          },
        };

        await handleSelectLocationInput(suggestion);
      },
      (e) => setError(e),
      {
        enableHighAccuracy: true,
        timeout: 8000,
      }
    );
  };

  /* =========================================================
     PICK SUGGESTION
     ========================================================= */

  const onPickSuggestion = (suggestion) => {
    if (suggestion?.properties?.source === 'current_location') {
      handleSelectCurrentPosition();
      return;
    }

    handleSelectLocationInput(suggestion);
  };

  /* =========================================================
     RENDER
     ========================================================= */

  return (
    <div className="mc-location-search">
      <label htmlFor="categoryProduct" className="mc-location-label">
        Adresse
      </label>

      <div className="mc-location-input-wrapper">
        {/* ICON */}
        <div className="mc-location-icon">
          <Icon name="location-outline" />
        </div>

        {/* INPUT */}
        <input
          type="text"
          id="categoryProduct"
          name="categoryProduct"
          className="mc-location-input"
          required
          value={searchTerm}
          onChange={handleInputChange}
          placeholder="Adresse, gare, aéroport..."
          autoComplete="off"
          onBlur={() => setTimeout(() => setShow(false), 200)}
          onFocus={() => {
            if (searchTerm.length >= 2) {
              setShow(true);
            }
          }}
        />

        {/* CLEAR */}
        {searchTerm && (
          <button
            type="button"
            className="mc-location-clear"
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClearInput}
            aria-label="Effacer la recherche"
          >
            <Icon name="close-outline" />
          </button>
        )}
      </div>

      {/* =====================================================
          RESULTS
      ===================================================== */}

      {searchTerm.length >= 2 && show && (
        <div className="mc-location-results">
          {/* LOADING */}
          {loading && (
            <div className="mc-location-state">
              <span className="mc-location-spinner"></span>
              <span>Recherche en cours...</span>
            </div>
          )}

          {/* ERROR */}
          {!loading && error && (
            <div className="mc-location-state mc-location-error">
              <Icon name="alert-circle-outline" />
              <span>Erreur lors de la recherche</span>
            </div>
          )}

          {/* EMPTY */}
          {!loading && !error && searchResults.length === 0 && (
            <div className="mc-location-state">
              <Icon name="search-outline" />
              <span>Aucun résultat trouvé</span>
            </div>
          )}

          {/* RESULTS */}
          {!loading && !error && searchResults.length > 0 && (
            <div className="mc-location-result-list">
              {searchResults.map((suggestion, index) => {
                const source = suggestion?.properties?.source;

                const isCurrentLocation = source === 'current_location';

                return (
                  <button
                    key={index}
                    type="button"
                    className={`mc-location-result ${
                      isCurrentLocation ? 'mc-location-current' : ''
                    }`}
                    onMouseDown={(event) => event.preventDefault()}
                    onClick={() => onPickSuggestion(suggestion)}
                  >
                    <span className="mc-location-result-icon">
                      <Icon name={getIconName(suggestion)} />
                    </span>

                    <span className="mc-location-result-content">
                      <span className="mc-location-result-title">
                        {suggestion.properties.full_address}
                      </span>

                      {isCurrentLocation && (
                        <span className="mc-location-result-subtitle">
                          Utiliser ma position actuelle
                        </span>
                      )}
                    </span>

                    <Icon className="mc-location-result-arrow" name="chevron-forward-outline" />
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default AutocompleteMapInput;
